import { canReadManagementWorkspace, type CurrentAuthorization } from '@/api/core/authorization';
import {
  setPropertyRecommendationWeight,
  type PropertyRecommendationWeight,
  type RecommendationWeightCommand,
  type RecommendationWeightReceipt,
} from '@/api/core/recommendation-weight';
import { isApiError } from '@/api/http/errors';
import { Button, DetailPage, DetailRow } from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type Dispatch, type SetStateAction } from 'react';
import type { PropertyRuntime } from './property-queries';
import { propertyRecommendationWeightQuery } from './property-queries';

export interface RecommendationWeightOwnerState {
  reviewed: PropertyRecommendationWeight | null;
  reviewedReadCount: number | null;
  reviewedQuery: WeakRef<object> | null;
  weightInput: string;
  intent: RecommendationWeightCommand | null;
  unknown: boolean;
  receipt: RecommendationWeightReceipt | null;
  message: string | null;
  pending: boolean;
}

export const emptyRecommendationWeightOwnerState: RecommendationWeightOwnerState = {
  reviewed: null,
  reviewedReadCount: null,
  reviewedQuery: null,
  weightInput: '0',
  intent: null,
  unknown: false,
  receipt: null,
  message: null,
  pending: false,
};

interface Props {
  propertyName: string;
  propertyId: string;
  ownerScopeKey?: string;
  runtime: PropertyRuntime;
  generation: number;
  authorization: CurrentAuthorization | null;
  isCurrent: () => boolean;
  ownerState?: RecommendationWeightOwnerState;
  setOwnerState?: Dispatch<SetStateAction<RecommendationWeightOwnerState>>;
}

function readErrorMessage(error: unknown) {
  if (isApiError(error)) {
    if (error.status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (error.status === 403) return '현재 계정에 추천 가중치 조회 권한이 없습니다.';
    if (error.status === 404) return '현재 지점의 추천 가중치 정보를 찾을 수 없습니다.';
    if (error.status === 409) return '추천 가중치가 변경되었습니다. 최신 값을 다시 확인해 주세요.';
    if (error.status === 503)
      return '추천 가중치를 지금 조회할 수 없습니다. 잠시 후 다시 시도해 주세요.';
    if (error.kind === 'invalid-response') return '추천 가중치 응답을 확인할 수 없습니다.';
  }
  return '추천 가중치를 불러오지 못했습니다. 현재 값을 다시 조회해 주세요.';
}

function writeErrorMessage(error: unknown) {
  if (isApiError(error)) {
    if (error.status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (error.status === 403) return '현재 계정에 추천 가중치 변경 권한이 없습니다.';
    if (error.status === 404) return '현재 지점의 추천 가중치 정보를 찾을 수 없습니다.';
    if (error.status === 409) return '현재 값이 변경되었습니다. 검토를 다시 시작해 주세요.';
    if (error.status === 422) return '입력값을 확인해 주세요. 저장하지 않았습니다.';
    if (error.status === 503) return '응답을 확인하지 못했습니다. 같은 요청을 다시 시도해 주세요.';
    if (error.kind === 'invalid-response')
      return '저장 응답을 확인할 수 없습니다. 같은 요청을 다시 확인해 주세요.';
  }
  return '응답을 확인하지 못했습니다. 같은 요청을 다시 시도해 주세요.';
}

const int32Input = /^-?(?:0|[1-9]\d*)$/;
function parseWeight(value: string): number | null {
  if (!int32Input.test(value)) return null;
  const weight = Number(value);
  return Number.isInteger(weight) && weight >= -2_147_483_648 && weight <= 2_147_483_647
    ? weight
    : null;
}

const displayWeight = (value: number) => String(value);

export function RecommendationWeightSection(props: Props) {
  const [localState, setLocalState] = useState(emptyRecommendationWeightOwnerState);
  const state = props.ownerState ?? localState;
  const setState = props.setOwnerState ?? setLocalState;
  const weightInput = state.weightInput;
  const queryClient = useQueryClient();
  const allowed =
    props.runtime.recommendationWeightEnabled === true &&
    props.authorization !== null &&
    canReadManagementWorkspace(props.authorization);
  const queryOptions = propertyRecommendationWeightQuery(
    props.runtime,
    props.generation,
    props.propertyId,
    allowed && props.isCurrent(),
    props.isCurrent,
    props.ownerScopeKey ?? 'standalone',
  );
  const query = useQuery(queryOptions);
  const currentQuery = queryClient.getQueryCache().find({ queryKey: queryOptions.queryKey });
  const currentReadCount = currentQuery?.state.dataUpdateCount ?? 0;
  const currentReadReady = query.status === 'success' && query.fetchStatus === 'idle';
  const reviewedProofCurrent =
    currentQuery !== undefined &&
    state.reviewedQuery?.deref() === currentQuery &&
    state.reviewedReadCount === currentReadCount;
  const reviewed = currentReadReady && reviewedProofCurrent ? state.reviewed : null;

  if (!allowed) return null;

  const updateState = (partial: Partial<RecommendationWeightOwnerState>) => {
    if (!props.isCurrent()) return;
    setState((current) => ({ ...current, ...partial }));
  };

  const reviewCurrent = async () => {
    if (!props.isCurrent() || state.pending || state.unknown) return;
    updateState({
      reviewed: null,
      reviewedReadCount: null,
      reviewedQuery: null,
      weightInput: '',
      message: null,
    });
    const response = await query.refetch();
    if (!props.isCurrent()) return;
    if (response.isError || !response.data) {
      updateState({
        reviewed: null,
        reviewedReadCount: null,
        reviewedQuery: null,
        weightInput: '',
        message: null,
      });
      return;
    }
    const reviewedQuery = queryClient.getQueryCache().find({ queryKey: queryOptions.queryKey });
    if (!reviewedQuery) {
      updateState({
        reviewed: null,
        reviewedReadCount: null,
        reviewedQuery: null,
        weightInput: '',
        message: null,
      });
      return;
    }
    updateState({
      reviewed: response.data,
      reviewedReadCount: reviewedQuery.state.dataUpdateCount,
      reviewedQuery: new WeakRef(reviewedQuery),
      weightInput: displayWeight(response.data.value),
      message: null,
    });
  };

  const refreshCurrent = async () => {
    if (!props.isCurrent() || state.pending || state.unknown) return;
    updateState({
      reviewed: null,
      reviewedReadCount: null,
      reviewedQuery: null,
      weightInput: '',
      message: null,
    });
    const response = await query.refetch();
    if (!props.isCurrent()) return;
    if (response.isError || !response.data)
      updateState({
        reviewed: null,
        reviewedReadCount: null,
        reviewedQuery: null,
        weightInput: '',
        message: null,
      });
  };

  const submit = async () => {
    if (!props.isCurrent() || state.pending) return;
    const reviewed = reviewedProofCurrent ? state.reviewed : null;
    const weight = parseWeight(weightInput);
    if (!state.intent && !currentReadReady) {
      updateState({ message: '현재 값을 다시 조회하고 검토해 주세요.' });
      return;
    }
    if (!state.intent && (!reviewed || weight === null)) {
      updateState({ message: '현재 값을 검토하고 정수 가중치를 입력해 주세요.' });
      return;
    }
    let command = state.intent;
    if (!command) {
      if (!reviewed || weight === null) {
        updateState({ message: '현재 값을 검토하고 정수 가중치를 입력해 주세요.' });
        return;
      }
      command = {
        command_id: crypto.randomUUID(),
        expected_revision: reviewed.revision,
        expected_fingerprint: reviewed.fingerprint,
        weight,
      };
    }
    updateState({ intent: command, pending: true, message: null });
    try {
      const receipt = await setPropertyRecommendationWeight(
        props.runtime.client,
        props.propertyId,
        command,
      );
      if (!props.isCurrent()) return;
      updateState({
        intent: null,
        unknown: false,
        pending: false,
        receipt,
        message: null,
      });
    } catch (error) {
      if (!props.isCurrent()) return;
      if (isApiError(error) && error.status === 409) {
        updateState({
          intent: null,
          unknown: false,
          pending: false,
          reviewed: null,
          reviewedReadCount: null,
          reviewedQuery: null,
          weightInput: '',
          message: writeErrorMessage(error),
        });
        return;
      }
      if (isApiError(error) && (error.status === 401 || error.status === 403)) {
        updateState({
          intent: null,
          unknown: false,
          pending: false,
          reviewed: null,
          reviewedReadCount: null,
          reviewedQuery: null,
          weightInput: '',
          receipt: null,
          message: writeErrorMessage(error),
        });
        return;
      }
      const outcomeMayBeUnknown =
        !isApiError(error) ||
        error.status === undefined ||
        error.status >= 500 ||
        (error.status === 200 && error.kind === 'invalid-response') ||
        error.kind === 'network' ||
        error.kind === 'timeout' ||
        error.kind === 'aborted';
      if (outcomeMayBeUnknown) {
        updateState({
          intent: command,
          unknown: true,
          pending: false,
          message: writeErrorMessage(error),
        });
        return;
      }
      updateState({
        intent: null,
        unknown: false,
        pending: false,
        message: writeErrorMessage(error),
      });
    }
  };

  return (
    <DetailPage.Section
      aria-label="추천 노출 가중치"
      title="추천 노출 가중치"
      description={`${props.propertyName}의 공개 추천 가중치입니다. 큰 값은 현재 공개 정렬 그룹 안에서 먼저 우선합니다.`}
    >
      <div className="flex flex-col gap-4">
        <p className="body text-muted-foreground">
          이용 가능 여부, 추천 날짜, 표시 가격이 먼저 적용됩니다. 이 값은 절대 순위나 예약을
          보장하지 않습니다.
        </p>
        <DetailRow
          label="마지막으로 확인한 현재 값"
          value={query.data && !query.isError ? displayWeight(query.data.value) : '확인 전'}
        />
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex min-w-48 flex-col gap-1" htmlFor="property-recommendation-weight">
            <span className="label">정수 가중치</span>
            <input
              id="property-recommendation-weight"
              className="border-input bg-background body h-10 rounded-md border px-3"
              inputMode="numeric"
              value={reviewed ? weightInput : ''}
              disabled={state.pending || state.unknown || !reviewed}
              onChange={(event) => {
                updateState({ weightInput: event.target.value });
              }}
              aria-describedby="property-recommendation-weight-range"
            />
          </label>
          <Button
            variant="outline"
            onClick={() => {
              void reviewCurrent();
            }}
            loading={query.isFetching}
            disabled={state.pending || state.unknown}
          >
            현재 값 검토
          </Button>
          {state.unknown ? (
            <Button
              onClick={() => {
                void submit();
              }}
              loading={state.pending}
              disabled={state.pending}
            >
              같은 요청 다시 보내기
            </Button>
          ) : (
            <Button
              onClick={() => {
                void submit();
              }}
              loading={state.pending}
              disabled={
                state.pending || !reviewed || !currentReadReady || parseWeight(weightInput) === null
              }
            >
              검토한 값 저장
            </Button>
          )}
        </div>
        <p id="property-recommendation-weight-range" className="caption text-muted-foreground">
          허용 범위는 -2,147,483,648부터 2,147,483,647까지입니다.
        </p>
        {reviewed && !state.unknown && (
          <div role="status" className="rounded-md border p-3">
            <p className="body-strong">저장 전 검토</p>
            <p className="body">
              현재 값 {String(reviewed.value)} · revision {String(reviewed.revision)}
            </p>
          </div>
        )}
        {state.receipt && (
          <div role="status" className="rounded-md border p-3">
            <p className="body-strong">저장 응답</p>
            <p className="body">
              {state.receipt.outcome === 'CHANGED' ? '값을 변경했습니다.' : '값이 이미 같았습니다.'}{' '}
              요청 결과 값 {String(state.receipt.value)} · result revision{' '}
              {String(state.receipt.result_revision)}
            </p>
            <p className="caption text-muted-foreground">
              이 응답은 해당 요청의 결과입니다. 현재 값은 다시 조회해 확인하세요.
            </p>
          </div>
        )}
        {state.message && (
          <p role="alert" className="body text-warning-foreground" aria-live="polite">
            {state.message}
          </p>
        )}
        {query.isError && (
          <div className="flex flex-wrap items-center gap-3">
            <p role="alert" className="body text-warning-foreground" aria-live="polite">
              {readErrorMessage(query.error)}
            </p>
            <Button
              variant="outline"
              onClick={() => {
                void refreshCurrent();
              }}
              loading={query.isFetching}
            >
              현재 값 다시 조회
            </Button>
          </div>
        )}
        {query.data && !state.unknown && (
          <Button
            variant="outline"
            onClick={() => {
              void refreshCurrent();
            }}
            loading={query.isFetching}
          >
            현재 값 새로 조회
          </Button>
        )}
      </div>
    </DetailPage.Section>
  );
}
